import { toBox } from "./qa/geometry";
import { koTimeline, endTransition } from "./ui/ko";
import {
  resultRects,
  resultPlayRect,
  rewardTimeline,
  resultRewards,
} from "./ui/results";
import {
  installTextRecorder,
  beginTextFrame,
  endTextFrame,
  lastTextRuns,
  qaTextRole,
} from "./qa/text-recorder";
import { GameStateMachine } from "./game";
import {
  MatchController,
  type MatchOutcome,
  type WinType,
} from "./game/match-controller";
import {
  DEFAULT_CONFIG,
  snowmanSpec,
  tutorialConfig,
  withSnowman,
  type GameConfig,
} from "./game/config";
import { applyHudEvent, newHudFx, type HudFx } from "./ui/hud-fx";
import { matchRound } from "./ui/match-hud";
import {
  advanceProgression,
  loadProgression,
  saveProgression,
  type Progression,
  type ProgressionStorage,
} from "./game/progression";
import type { RuntimePlatform, SafeInsets } from "./platform/types";
import { GameRenderer } from "./render/game-renderer";
import { HUDRenderer } from "./render/hud-renderer";
import { loadCanvasFonts } from "./render/fonts";
import { InputManager } from "./input/input-manager";
import { createDefaultConfig } from "./input/config";
import type { InputSample } from "./input/types";
import type { PointerInput, PointerSample } from "./input/pointer";
import {
  computeGameViewport,
  computeLayoutRects,
  pointInRect,
} from "./ui/layout";
import { AudioManager, HapticsManager } from "./audio";

export interface Application {
  resize(
    width: number,
    height: number,
    pixelRatio: number,
    safeInsets: SafeInsets,
  ): void;
}

export type ForfeitConfirmationAction = "open" | "cancel" | "confirm";

export function resolveForfeitConfirmation(action: ForfeitConfirmationAction): {
  confirming: boolean;
  forfeit: boolean;
} {
  if (action === "open") return { confirming: true, forfeit: false };
  if (action === "confirm") return { confirming: false, forfeit: true };
  return { confirming: false, forfeit: false };
}

export function updateActiveMatch(
  states: GameStateMachine,
  matchController: MatchController,
  deltaMs: number,
): void {
  if (states.state.kind !== "match") return;
  matchController.update(deltaMs);
  if (states.state.kind === "match") {
    states.updateMatch(matchController.getState());
  }
}

export function registerMatchInput(
  input: PointerInput,
  getManager: () => Pick<InputManager, "processSample"> | null,
  now: () => number,
): void {
  const route = (phase: InputSample["phase"]) => (sample: PointerSample) => {
    getManager()?.processSample({
      x: sample.x,
      y: sample.y,
      t: now(),
      pointerId: sample.id,
      phase,
    });
  };
  input.onStart(route("start"));
  input.onMove(route("move"));
  input.onEnd(route("end"));
  input.onCancel(route("cancel"));
}

export function startApplication(platform: RuntimePlatform): Application {
  const states = new GameStateMachine();
  const renderer = new GameRenderer(platform);
  const hudRenderer = new HUDRenderer(platform.width, platform.height);
  const audioManager = new AudioManager();
  const hapticsManager = new HapticsManager();
  hapticsManager.setEnabled(!platform.reduceMotion);
  if (platform.debug || platform.qa) {
    window.__frameErrors = 0;
  }

  // Calculate viewport for proper touch mapping
  let currentViewport = computeGameViewport(
    platform.width,
    platform.height,
    platform.safeInsets,
  );
  let currentLayout = computeLayoutRects(
    currentViewport,
    platform.safeInsets,
    platform.width,
    platform.height,
  );
  let matchController: MatchController | null = null;
  let inputManager: InputManager | null = null;
  let audioUnlocked = false;
  let confirmingForfeit = false;
  let qaManual = false;
  let qaStepping = false;
  const qaTickCallbacks = new Set<() => void>();
  const qaScheduled = new Map<number, Record<string, unknown>[]>();
  let qaFrozenTime: number | null = null;
  let qaArmed: { phase: string; t: number } | null = null;
  let resultStartedAt: number | null = null;
  let resultTime = 0;
  let presentation: {
    outcome: MatchOutcome;
    winType: WinType;
    startedAt: number;
    worldBase: number;
    startTick: number;
    swapped: boolean;
  } | null = null;
  let presentationTime = 0;
  let worldClock = 0,
    lastWorldTime = 0;
  let qaHidden = false;
  const frameTimes: number[] = [];
  let coinTick = 0;
  let buttonPress:
    { id: string; at: number; releasedAt: number | null } | undefined;
  let hudFx: HudFx = newHudFx();
  let matchConfig: GameConfig = DEFAULT_CONFIG;
  if (platform.debug || platform.qa) {
    window.__snowballDebug = {
      getState: () => states.state,
      getLayoutRects: () => currentLayout,
      getRenderStats: () => renderer.getRenderStats(),
      getBounds: (id: string) => renderer.getBounds(id),
      getFrameCount: () => window.__renderedFrames ?? 0,
      ...(platform.debug
        ? {
            advanceMatch: (seconds: number) => {
              if (!matchController) return;
              for (let elapsed = 0; elapsed < seconds * 1_000; elapsed += 100) {
                updateActiveMatch(states, matchController, 100);
              }
            },
            forfeitMatch: () => matchController?.forfeit(),
          }
        : {}),
    };
    window.__renderedFrames = 0;
  }
  registerMatchInput(
    platform.input,
    () => inputManager,
    () => ((matchController?.getState().tick ?? 0) * 1000) / 60,
  );

  const mapToDesignSpace = (sample: InputSample): InputSample => ({
    ...sample,
    x: (sample.x - currentViewport.x) / currentViewport.scale,
    y: (sample.y - currentViewport.y) / currentViewport.scale,
  });

  // Audio IDs to preload (after first touch)
  const audioIds = [
    "ui_tap",
    "countdown",
    "gather_crunch",
    "gather_full",
    "roll_loop",
    "roll_tier",
    "throw_s",
    "throw_m",
    "throw_l",
    "windup_tell",
    "hit_s",
    "hit_m",
    "hit_l",
    "got_hit",
    "dodge",
    "block",
    "block_perfect",
    "block_crack",
    "round_start",
    "tick",
    "ko",
    "mus_title_loop",
    "mus_match_loop",
    "stg_win",
    "stg_lose",
  ];

  // Ladder progression (persisted). QA runs always use the default config so
  // device baselines and e2e fixtures stay deterministic.
  const progressionStorage: ProgressionStorage | undefined = (() => {
    try {
      return platform.qa ? undefined : globalThis.localStorage;
    } catch {
      return undefined;
    }
  })();
  let progression: Progression = loadProgression(progressionStorage);

  // First-run practice match. Real devices run it until it is finished or
  // skipped once; QA only runs it when asked (?qaTutorial=run) so the rest
  // of the suite keeps its deterministic full match.
  const tutorialQuery = new URLSearchParams(window.location.search);
  const tutorialStore = (() => {
    try {
      return globalThis.localStorage;
    } catch {
      return undefined;
    }
  })();
  if (!platform.qa && tutorialQuery.get("tutorial") === "replay") {
    try {
      tutorialStore?.removeItem("tutorialSeen");
    } catch {
      /* Best effort. */
    }
  }
  let tutorialPending = platform.qa
    ? tutorialQuery.get("qaTutorial") === "run"
    : (() => {
        try {
          return tutorialStore?.getItem("tutorialSeen") !== "true";
        } catch {
          return true;
        }
      })();
  let tutorialRun = false;
  let tutorialExitAt: number | null = null;
  const endTutorial = () => {
    tutorialPending = false;
    tutorialRun = false;
    tutorialExitAt = null;
    hudRenderer.matchHUD.finish();
  };
  const skipTutorial = () => {
    if (!tutorialRun) return;
    endTutorial();
    startNewMatch();
  };
  hudRenderer.matchHUD.onSkip = skipTutorial;

  const startNewMatch = () => {
    tutorialRun = tutorialPending;
    tutorialExitAt = null;
    worldClock = 0;
    lastWorldTime = 0;
    coinTick = 0;
    presentation = null;
    buttonPress = undefined;
    hudFx = newHudFx();
    matchConfig = tutorialRun
      ? tutorialConfig(DEFAULT_CONFIG)
      : platform.qa
        ? DEFAULT_CONFIG
        : withSnowman(DEFAULT_CONFIG, progression.level);
    matchController = new MatchController(matchConfig, platform.qaSeed);
    if (platform.qa) {
      qaScheduled.clear();
      const controller = matchController;
      controller.qaBeforeTick = (tick) => {
        for (const action of qaScheduled.get(tick) ?? [])
          controller.processPlayerAction({ ...action, tick } as never);
        qaScheduled.delete(tick);
      };
      controller.qaAfterTick = () => {
        for (const callback of qaTickCallbacks) callback();
      };
    }
    states.startMatch(matchController.getState());

    // Stop title music, start match music
    audioManager.stopMusic();
    audioManager.play("mus_match_loop");

    // Set up input in design space (360x640)
    const inputConfig = createDefaultConfig(platform.width, platform.height);

    inputConfig.dodgeButton = currentLayout.dodge;
    inputConfig.blockButton = currentLayout.block;

    inputManager = new InputManager(
      inputConfig,
      () => matchController?.getState().tick || 0,
      mapToDesignSpace,
    );

    // Wire input to match controller
    inputManager.onInput((action) => {
      if (presentation || matchController?.getState().paused) return;
      matchController?.processPlayerAction(action);
      hudRenderer.matchHUD.input(
        action.type,
        action.type === "rollProgress" ? action.radians : undefined,
      );

      // Play sounds for actions
      if (action.type === "dodge") {
        audioManager.play("dodge");
        hapticsManager.light();
      } else if (action.type === "block") {
        audioManager.play("block");
        hapticsManager.medium();
      } else if (action.type === "throw") {
        // Windup sound on press, throw_tier sound will play on actual throw event
        audioManager.play("windup_tell");
        hapticsManager.medium();
      } else if (action.type === "gatherStroke") {
        audioManager.play("gather_crunch");
      } else if (action.type === "rollLap") {
        audioManager.play("roll_tier");
      }
    });

    matchController.on((event) => {
      {
        const snap = matchController?.getState();
        if (snap)
          applyHudEvent(hudFx, event, snap.tick, snap.projectiles, {
            dodgeWindow:
              matchConfig.dodgeIFramesDuration * matchConfig.tickRate,
            blockWindow: matchConfig.blockWindowDuration * matchConfig.tickRate,
          });
      }
      if (event.type === "tutorialComplete" && tutorialRun) {
        tutorialExitAt = lastTime + 1400;
        audioManager.play("stg_win");
      }
      if (event.type === "matchResolved") {
        hudRenderer.matchHUD.finish();
        const final = matchController!.getState();
        if (tutorialRun) endTutorial(); // forfeited the practice match
        if (!platform.qa) {
          progression = advanceProgression(
            progression,
            event.outcome,
            event.winType,
          );
          saveProgression(progressionStorage, progression);
        }
        states.awardCoins(
          resultRewards({
            outcome: event.outcome,
            winType: event.winType,
            playerHP: final.player.hp,
            opponentHP: final.opponent.hp,
          }).coins,
        );
        if (states.state.kind === "match" && !presentation) {
          presentation = {
            outcome: event.outcome,
            winType: event.winType,
            startedAt: lastTime,
            worldBase: worldClock,
            startTick: states.state.match.tick,
            swapped: false,
          };
          states.state.match.phase = "resolve";
          states.state.match.phaseStartTick = states.state.match.tick;
          states.state.match.projectiles = [];
          inputManager?.setActive(null);
        }
        // Stop match music, play result stinger
        audioManager.stopMusic();
        audioManager.play(event.outcome === "win" ? "stg_win" : "stg_lose");
        if (event.outcome === "win") {
          hapticsManager.heavy();
        }
      }
      // Play throw sound with tier on actual throw
      if (event.type === "throw") {
        audioManager.play("throw_s");
      }
      // Switch input mode based on phase
      if (event.type === "phaseChange") {
        // Play round start sound
        if (event.phase.startsWith("r")) {
          audioManager.play("round_start");
        }

        switch (event.phase) {
          case "countdown":
            audioManager.play("countdown");
            // Wait for match music preload, then start
            void audioManager.preload(["mus_match_loop"]).then(() => {
              audioManager.play("mus_match_loop");
            });
            break;
          case "r1_gather":
            inputManager?.setActive("gather");
            break;
          case "r2_roll":
            inputManager?.setActive("roll");
            audioManager.play("roll_loop");
            break;
          case "r3_throw":
          case "sudden_death":
            inputManager?.setActive("throw");
            audioManager.stop("roll_loop");
            break;
          default:
            inputManager?.setActive(null);
            break;
        }
      }
      // Play hit sounds - got_hit for player hit, hit_tier for opponent hit
      if (event.type === "hit") {
        if (event.target === "player") {
          audioManager.play("got_hit");
          hapticsManager.heavy();
        } else {
          audioManager.play("hit_s");
        }
      }
    });

    matchController.startMatch();
  };

  platform.input.onEnd(() => {
    if (buttonPress)
      buttonPress.releasedAt =
        ((matchController?.getState().tick ?? 0) * 1000) / 60;
  });
  // Handle button clicks on menu and results screens
  platform.input.onStart((sample) => {
    if (presentation) return;
    hapticsManager.unlock();
    if (
      states.state.kind === "match" &&
      (states.state.match.phase === "r3_throw" ||
        states.state.match.phase === "sudden_death")
    )
      for (const id of ["dodge", "block"] as const)
        if (pointInRect(sample.x, sample.y, currentLayout[id]))
          buttonPress = {
            id,
            at: (states.state.match.tick * 1000) / 60,
            releasedAt: null,
          };
    // Unlock audio on first touch
    if (!audioUnlocked) {
      void audioManager.unlock().then(async () => {
        // Preload all audio after unlock
        await audioManager.preload(audioIds);
        // Start title music after preload
        audioManager.play("mus_title_loop");
        audioUnlocked = true;
      });
    }

    audioManager.play("ui_tap");
    hapticsManager.light();

    if (states.state.kind === "match" && matchController) {
      if (pointInRect(sample.x, sample.y, currentLayout.pause)) {
        matchController.pause();
        confirmingForfeit = false;
        return;
      }

      const matchState = matchController.getState();
      if (matchState.paused) {
        if (confirmingForfeit) {
          if (pointInRect(sample.x, sample.y, currentLayout.resume)) {
            confirmingForfeit = resolveForfeitConfirmation("cancel").confirming;
            return;
          }
          if (pointInRect(sample.x, sample.y, currentLayout.forfeit)) {
            confirmingForfeit = false;
            matchController.resume();
            matchController.forfeit();
            return;
          }
          return;
        }
        if (pointInRect(sample.x, sample.y, currentLayout.resume)) {
          matchController.resume();
          return;
        }

        if (pointInRect(sample.x, sample.y, currentLayout.forfeit)) {
          confirmingForfeit = resolveForfeitConfirmation("open").confirming;
          return;
        }

        return;
      }
    }

    if (pointInRect(sample.x, sample.y, currentLayout.mute)) {
      audioManager.toggleMute();
      return;
    }

    if (
      states.state.kind === "menu" &&
      pointInRect(sample.x, sample.y, currentLayout.play)
    ) {
      startNewMatch();
    }

    if (states.state.kind === "results") {
      const R = resultRects(
        platform.width,
        platform.height,
        platform.safeInsets,
      );
      if (
        rewardTimeline(resultTime, platform.reduceMotion).playVisible &&
        rewardTimeline(resultTime, platform.reduceMotion).playAlpha > 0 &&
        pointInRect(
          sample.x,
          sample.y,
          resultPlayRect(
            platform.width,
            platform.height,
            platform.safeInsets,
            resultTime,
            platform.reduceMotion,
          ),
        )
      ) {
        states.returnToMenu();
        startNewMatch();
        return;
      }
      if (
        rewardTimeline(resultTime, platform.reduceMotion).homeAlpha > 0 &&
        pointInRect(sample.x, sample.y, R.home)
      ) {
        states.returnToMenu();
        inputManager?.setActive(null);
        audioManager.stopMusic();
        audioManager.play("mus_title_loop");
        return;
      }
    }
  });

  if (platform.qa) {
    installTextRecorder();
    const phase = () => {
      if (presentation)
        return presentation.winType === "KO" ? "ko" : "roundEnd";
      const s = states.state;
      if (s.kind === "menu" || s.kind === "boot") return "home";
      if (s.kind === "results") return s.outcome;
      if (s.match.paused) return "pause";
      if (s.match.phase.startsWith("banner"))
        return s.match.phase !== "banner_r1" &&
          ((s.match.tick - s.match.phaseStartTick) * 1000) / 60 < 900
          ? "roundStinger"
          : "roundEnd";
      if (/r1|intro|countdown/.test(s.match.phase)) return "r1";
      if (/r2|roll/.test(s.match.phase)) return "r2";
      if (s.match.phase === "resolve")
        return s.match.winType === "KO" ? "ko" : "roundEnd";
      return "r3";
    };
    const clock = () =>
      presentation
        ? presentationTime
        : states.state.kind === "results"
          ? resultTime
          : states.state.kind === "match"
            ? states.state.match.paused
              ? (qaFrozenTime ?? 0)
              : ((states.state.match.tick - states.state.match.phaseStartTick) *
                  1000) /
                  60 -
                (states.state.match.phase.startsWith("banner") &&
                states.state.match.phase !== "banner_r1"
                  ? 900
                  : 0)
            : 0;
    const uiIds = [
      "home.logo",
      "home.coins",
      "home.coinIcon",
      "btn.play",
      "results.podium",
      "results.topFace",
      "results.topEllipse",
      "results.sideWall",
      "results.dizzyStar",
      "results.dizzyStar.0",
      "results.dizzyStar.1",
      "results.dizzyStar.2",
      "results.heroBase",
      "results.heroSurface",
      "results.sideBase",
      "results.sideSurface",
      "results.sideShadow",
      "results.heroBody",
      "results.sideFace",
      "results.sideBody",
      "results.sideHead",
      "results.sideHat",
      "results.dizzyStars",
      "results.winner",
      "results.opponent",
      "results.ribbon",
      "results.subtitle",
      "results.chips",
      "results.chip.0",
      "results.chip.1",
      "results.chip.2",
      "results.icon.0",
      "results.icon.1",
      "results.icon.2",
      "results.chest",
      "results.rewards",
      "results.xp",
      "results.xp.fill",
      "btn.playAgain",
      "btn.home",
      "hud.badge.opp",
      "hud.badge.you",
      "hud.timer",
      "hud.dots",
      "hud.roundLabel",
      "overlay.pauseSheet",
      "hud.pause",
      "hud.mute",
      "btn.dodge",
      "btn.block",
      "btn.dodge.label",
      "btn.block.label",
      "hud.ammo",
      "hud.hint",
      "hud.ko",
      "hud.banner",
      "hud.banner.subtitle",
      "btn.resume",
      "btn.forfeit",
    ];
    const unfreeze = () => {
      if (qaFrozenTime !== null) {
        if (presentation) presentation.startedAt = lastTime - qaFrozenTime;
        if (states.state.kind === "results")
          resultStartedAt = lastTime - qaFrozenTime;
        worldClock = lastWorldTime;
      }
      qaFrozenTime = null;
    };
    window.__qa = {
      resultsFacing: () => ({
        hero: renderer.facingDot("hero"),
        side: renderer.facingDot("side"),
      }),
      manual: (enabled) => {
        qaManual = enabled;
      },
      step: (n) => {
        if (!Number.isInteger(n) || n < 0)
          throw new Error("Invalid tick count");
        qaStepping = true;
        try {
          for (let i = 0; i < n; i++) frame(lastTime + 1000 / 60);
        } finally {
          qaStepping = false;
        }
      },
      phase,
      t: clock,
      unbuilt: () => ["hud.rotate"],
      hasParticles: () => true,
      rewards: () =>
        states.state.kind === "results"
          ? resultRewards({
              ...states.state,
              playerHP: matchController?.getState().player.hp ?? 0,
              opponentHP: matchController?.getState().opponent.hp ?? 0,
            })
          : null,
      frozen: () => qaFrozenTime !== null,
      freezeAt: (phase, t) => {
        qaArmed = { phase, t };
      },
      hideHud: (hidden = true) => hudRenderer.setHidden(hidden),
      ids: () => [...new Set([...renderer.worldIds(), ...uiIds])],
      bbox: (id) => {
        if (qaHidden) return null;
        if (id === "hud.banner" && phase() !== "roundEnd") return null;
        if (id === "hud.stinger" && phase() !== "roundStinger") return null;
        if (id === "btn.play")
          return states.state.kind === "menu"
            ? toBox(currentLayout.play)
            : null;
        if (
          id === "results.winner" ||
          id === "results.opponent" ||
          /^results\.(topFace|topEllipse|sideWall|heroBase|heroSurface|sideBase|sideSurface|sideShadow)$/.test(
            id,
          ) ||
          id === "results.podium" ||
          id === "results.dizzyStar" ||
          /^results\.dizzyStar\.[012]$/.test(id) ||
          id === "results.heroBody" ||
          id === "results.sideFace" ||
          id === "results.sideBody" ||
          id === "results.sideHead" ||
          id === "results.sideHat" ||
          id === "results.dizzyStars"
        )
          return states.state.kind === "results"
            ? toBox(renderer.getBounds(id))
            : null;
        if (/^(hud|btn|results|overlay)\./.test(id))
          return toBox(hudRenderer.getBounds(id));
        return toBox(renderer.getBounds(id));
      },
      hitRect: (id) => {
        if (qaHidden) return null;
        const s = states.state;
        if (s.kind === "menu")
          return id === "btn.play" ? toBox(currentLayout.play) : null;
        if (s.kind === "results") {
          if (!hudRenderer.getBounds(id)) return null;
          if (id === "btn.playAgain")
            return toBox(
              resultPlayRect(
                platform.width,
                platform.height,
                platform.safeInsets,
                resultTime,
                platform.reduceMotion,
              ),
            );
          return id === "btn.home"
            ? toBox(
                resultRects(
                  platform.width,
                  platform.height,
                  platform.safeInsets,
                ).home,
              )
            : null;
        }
        if (s.kind !== "match" || presentation) return null;
        const map: Record<string, keyof typeof currentLayout> = s.match.paused
          ? { "btn.resume": "resume", "btn.forfeit": "forfeit" }
          : {
              "hud.pause": "pause",
              "hud.mute": "mute",
              "btn.dodge": "dodge",
              "btn.block": "block",
            };
        return map[id] ? toBox(currentLayout[map[id]]) : null;
      },
      textSizes: () =>
        qaHidden
          ? []
          : lastTextRuns().map((r) => ({
              id: r.id,
              text: r.text,
              pt: r.pt,
              color: r.color,
              stroke: r.strokeColor ?? null,
              role: qaTextRole(r.text),
              strokeColor: r.strokeColor,
              strokeWidth: r.strokeWidth,
              bbox: { x: r.x, y: r.y, w: r.w, h: r.h },
            })),
      opponentName: () => "Mika",
      particles: (kind) => (qaHidden ? 0 : renderer.particleCount(kind)),
      stats: () => {
        const s = renderer.getRenderStats();
        return {
          drawCalls: s.drawCalls,
          tris: s.triangles,
          fps:
            frameTimes.length > 1
              ? ((frameTimes.length - 1) * 1000) /
                (frameTimes.at(-1)! - frameTimes[0]!)
              : 0,
        };
      },
      safe: () => ({
        t: platform.safeInsets.top,
        b: platform.safeInsets.bottom,
        l: platform.safeInsets.left,
        r: platform.safeInsets.right,
      }),
      hideAll: (hidden = true) => {
        qaHidden = hidden;
        renderer.hideAll(hidden);
        hudRenderer.setHidden(hidden);
      },
      koFall: () => renderer.knockoutFall(),
      timeScale: () =>
        presentation?.winType === "KO"
          ? koTimeline(presentationTime, platform.reduceMotion).timeScale
          : states.state.kind === "match" && states.state.match.paused
            ? 0
            : 1,
      freeze: (t) => {
        if (t === false || t === null) {
          unfreeze();
          return { ok: true, t: clock() };
        }
        if (
          typeof t === "number" &&
          states.state.kind === "results" &&
          presentation
        ) {
          worldClock = lastWorldTime;
          presentation = null;
          resultStartedAt = lastTime - t;
        }
        const target = t === true ? clock() : Math.max(0, t);
        if (
          !presentation &&
          states.state.kind === "match" &&
          !states.state.match.paused &&
          matchController
        ) {
          const initialPhase = states.state.match.phase;
          qaFrozenTime = null;
          for (let i = 0; i < 5000 && clock() + 1000 / 120 < target; i++) {
            window.__qa!.step(1);
            if (
              presentation ||
              states.state.kind !== "match" ||
              states.state.match.phase !== initialPhase
            )
              break;
          }
        }
        qaFrozenTime = target;
        // Measurement hooks must reflect the frozen frame synchronously.
        frame(lastTime, true);
        if (states.state.kind === "results") {
          // A seek may finish the match presentation or capture the backdrop.
          // Complete that transition and render the requested results time now,
          // so measurements and screenshots never depend on another RAF.
          presentation = null;
          qaFrozenTime = target;
          resultStartedAt = lastTime - target;
          frame(lastTime, true);
        }
        return { ok: true, t: qaFrozenTime };
      },
      unfreeze,
      worldImage: () => renderer.captureWorld(),
      knockout: (loser = "opponent") => {
        if (states.state.kind === "menu") startNewMatch();
        if (states.state.kind !== "match" || !matchController) return;
        qaFrozenTime = null;
        const m = states.state.match;
        m.paused = false;
        m.phase = "r3_throw";
        m.phaseStartTick = m.tick;
        m[loser].hp = 10; // one 10-damage ball is a KO
        const winner = loser === "player" ? "opponent" : "player";
        m[winner].hp = 70;
        m[winner].damageDealt = 85;
        m[winner].hits = 3;
        m[winner].blocks = 2;
        m[loser].damageDealt = 30;
        m[loser].hits = 1;
        m.projectiles = [
          {
            id: m.nextProjectileId++,
            perfect: false,
            thrower: loser === "player" ? "opponent" : "player",
            target: loser,
            lane: "center",
            damage: 10,
            startTick: m.tick,
            endTick: m.tick + 1,
          },
        ];
        updateActiveMatch(states, matchController, 1000 / 60);
      },
    };
    window.__snowballQA = {
      onTick: (callback) => {
        qaTickCallbacks.add(callback);
        return () => {
          qaTickCallbacks.delete(callback);
        };
      },
      schedule: (tick, action) => {
        if (
          !Number.isInteger(tick) ||
          tick <= (matchController?.getState().tick ?? 0)
        )
          throw new Error("Scheduled tick must be in the future");
        qaScheduled.set(tick, [...(qaScheduled.get(tick) ?? []), action]);
      },
      summary: () => {
        const s = states.state,
          m = matchController?.getState();
        return {
          kind: s.kind,
          phase: s.kind === "match" ? s.match.phase : s.kind,
          round: m
            ? /r1|intro|countdown/.test(m.phase)
              ? 1
              : /r2|roll/.test(m.phase)
                ? 2
                : 3
            : 0,
          remaining:
            s.kind === "match" && m
              ? Math.max(
                  0,
                  ((
                    {
                      countdown: DEFAULT_CONFIG.countdownDuration,
                      banner_r1: DEFAULT_CONFIG.bannerDuration,
                      banner_r2: DEFAULT_CONFIG.bannerDuration,
                      banner_r3: DEFAULT_CONFIG.bannerDuration,
                      r1_gather: DEFAULT_CONFIG.roundGatherDuration,
                      r2_roll: DEFAULT_CONFIG.roundRollDuration,
                      r3_throw: DEFAULT_CONFIG.roundThrowDuration,
                      sudden_death: DEFAULT_CONFIG.suddenDeathDuration,
                    } as Record<string, number>
                  )[m.phase] ?? 0) -
                    (m.tick - m.phaseStartTick) / 60,
                )
              : 0,
          tick: m?.tick ?? 0,
          playerDamage: m?.player.damageDealt ?? 0,
          playerHits: m?.player.hits ?? 0,
          playerBlocks: m?.player.blocks ?? 0,
          rewards: window.__qa?.rewards() ?? null,
          paused: m?.paused ?? false,
          hitEffects: m?.hitEffects ?? [],
          projectiles: m?.projectiles.length ?? 0,
          playerHP: m?.player.hp ?? null,
          opponentHP: m?.opponent.hp ?? null,
          playerSnow: m?.player.snow ?? null,
          playerAmmo: m?.player.ammo ?? null,
          playerLane: m?.playerLane ?? null,
          opponentLane: m?.opponentLane ?? null,
          incoming: (m?.projectiles ?? [])
            .filter((p) => p.target === "player")
            .map((p) => ({ lane: p.lane, endTick: p.endTick })),
          winner:
            s.kind === "results"
              ? s.outcome === "win"
                ? "player"
                : s.outcome === "lose"
                  ? "opponent"
                  : "draw"
              : null,
          winType: s.kind === "results" ? s.winType : null,
        };
      },
      start: () => {
        if (states.state.kind === "menu") startNewMatch();
      },
      act: (action) => {
        if (
          states.state.kind === "match" &&
          !states.state.match.paused &&
          !presentation
        )
          matchController?.processPlayerAction({
            ...action,
            tick: states.state.match.tick,
          } as never);
      },
      freeze: (frozen) => {
        if (frozen) window.__qa?.freeze(clock());
        else unfreeze();
      },
      pause: () => matchController?.pause(),
      resume: () => matchController?.resume(),
      forceResult: (playerHP, opponentHP, how) => {
        if (how.toLowerCase() === "ko") {
          window.__qa?.knockout(playerHP < opponentHP ? "player" : "opponent");
          return;
        }
        if (states.state.kind !== "match") return;
        const m = states.state.match;
        m.player.hp = playerHP;
        m.opponent.hp = opponentHP;
        m.player.damageDealt = 100 - opponentHP;
        m.opponent.damageDealt = 100 - playerHP;
        m.player.hits = Math.ceil(m.player.damageDealt / 30);
        m.opponent.hits = Math.ceil(m.opponent.damageDealt / 30);
        m.roundsWon =
          playerHP > opponentHP
            ? [1, 1]
            : playerHP < opponentHP
              ? [1, 1]
              : [0, 0];
        m.player.blocks = 2;
        m.opponent.blocks = 1;
        const outcome =
          how === "draw"
            ? "draw"
            : how === "forfeit" || playerHP < opponentHP
              ? "lose"
              : playerHP > opponentHP
                ? "win"
                : "draw";
        const winType =
          how === "KO" ? "KO" : how === "forfeit" ? "forfeit" : "decision";
        presentation = null;
        qaFrozenTime = null;
        resultStartedAt = lastTime;
        m.outcome = outcome;
        m.winType = winType;
        hudRenderer.matchHUD.finish();
        states.finishMatch(outcome, winType);
      },
      framing: () => renderer.getFraming(),
      layout: () => ({
        rects: currentLayout,
        viewport: currentViewport,
        safeInsets: platform.safeInsets,
      }),
      hideAll: (hidden) => window.__qa?.hideAll(hidden),
    };
  }
  let lastTime = 0;
  const frame = (time: number, renderOnly = false) => {
    if (qaManual && !qaStepping) time = lastTime;
    try {
      if (lastTime === 0) {
        if (presentation) presentation.startedAt = time;
        if (resultStartedAt !== null) resultStartedAt = time;
      }
      const deltaMs = qaStepping
        ? 1000 / 60
        : lastTime === 0
          ? 0
          : time - lastTime;
      lastTime = time;
      if (tutorialExitAt !== null && time >= tutorialExitAt && !renderOnly) {
        endTutorial();
        startNewMatch();
      }
      if (platform.qa) {
        frameTimes.push(time);
        while (frameTimes.length > 2 && frameTimes[0]! < time - 3000)
          frameTimes.shift();
      }
      if (
        qaFrozenTime === null &&
        !platform.freezeTime &&
        !presentation &&
        !(states.state.kind === "match" && states.state.match.paused)
      )
        worldClock += Math.max(0, deltaMs);

      // Update match if active
      if (
        matchController &&
        states.state.kind === "match" &&
        !platform.freezeTime &&
        qaFrozenTime === null &&
        !presentation &&
        !states.state.match.paused
      ) {
        let remaining =
          deltaMs * (platform.qa && !qaManual ? (platform.qaSpeed ?? 1) : 1);
        while (remaining > 0) {
          const step = Math.min(100, remaining);
          updateActiveMatch(states, matchController, step);
          remaining -= step;
          if (presentation || states.state.kind !== "match") break;
        }
      }

      if (
        qaArmed &&
        window.__qa?.phase() === qaArmed.phase &&
        window.__qa.t() >= qaArmed.t
      ) {
        const target = qaArmed.t;
        qaArmed = null;
        window.__qa.freeze(target);
      }
      let worldTime = qaFrozenTime ?? worldClock;
      if (presentation) {
        presentationTime =
          qaFrozenTime ?? Math.max(0, time - presentation.startedAt);
        const isKO = presentation.winType === "KO",
          transition = endTransition(
            presentationTime,
            isKO,
            platform.reduceMotion,
          );
        const worldMs = isKO
          ? koTimeline(presentationTime, platform.reduceMotion).worldMs
          : presentationTime;
        worldTime = presentation.worldBase + worldMs;
        if (presentationTime >= transition.swapAt && !presentation.swapped) {
          states.finishMatch(presentation.outcome, presentation.winType);
          presentation.swapped = true;
          resultStartedAt = presentation.startedAt + transition.total;
        }
        if (presentationTime >= transition.total) {
          worldClock = worldTime;
          if (qaFrozenTime !== null) {
            qaFrozenTime = 0;
            resultStartedAt = time;
          }
          presentation = null;
        }
      }
      if (platform.qa) beginTextFrame();
      if (states.state.kind === "results") {
        resultStartedAt ??= time;
        resultTime = presentation
          ? 0
          : (qaFrozenTime ?? Math.max(0, time - resultStartedAt));
      } else resultStartedAt = null;
      // Render
      lastWorldTime = worldTime;
      // Presentation uses a derived render tick; never overwrite the absolute
      // simulation tick with a fractional slow-motion clock.
      const renderState =
        presentation && states.state.kind === "match"
          ? {
              ...states.state,
              match: {
                ...states.state.match,
                tick:
                  presentation.startTick +
                  ((worldTime - presentation.worldBase) * 60) / 1000,
              },
            }
          : states.state;
      renderer.render(
        worldTime,
        renderState,
        resultTime,
        presentation
          ? {
              t: presentationTime,
              ko: presentation.winType === "KO",
              loser: presentation.outcome === "win" ? "opponent" : "player",
            }
          : undefined,
        !qaStepping || renderOnly,
        !renderOnly && (qaManual || qaFrozenTime !== null),
      );
      if (platform.debug || platform.qa) {
        window.__renderedFrames = (window.__renderedFrames ?? 0) + 1;
      }

      // Render HUD based on game state
      if (states.state.kind === "match" && matchController) {
        const matchState =
          renderState.kind === "match"
            ? renderState.match
            : matchController.getState();
        const elapsed =
          (matchState.tick - matchState.phaseStartTick) /
          DEFAULT_CONFIG.tickRate;
        let phaseDuration = 0;

        switch (matchState.phase) {
          case "countdown":
            phaseDuration = DEFAULT_CONFIG.countdownDuration;
            break;
          case "r1_gather":
            phaseDuration = DEFAULT_CONFIG.roundGatherDuration;
            break;
          case "r2_roll":
            phaseDuration = DEFAULT_CONFIG.roundRollDuration;
            break;
          case "r3_throw":
            phaseDuration = DEFAULT_CONFIG.roundThrowDuration;
            break;
          case "sudden_death":
            phaseDuration = DEFAULT_CONFIG.suddenDeathDuration;
            break;
          case "banner_r1":
          case "banner_r2":
          case "banner_r3":
            phaseDuration = DEFAULT_CONFIG.bannerDuration;
            break;
        }

        // Play tick sound in last 3 seconds
        const timeLeft = phaseDuration - elapsed;
        const prevTimeLeft = phaseDuration - (elapsed - deltaMs / 1000);
        if (
          timeLeft <= 3 &&
          timeLeft > 0 &&
          Math.floor(timeLeft) < Math.floor(prevTimeLeft)
        ) {
          audioManager.play("tick");
        }

        // Calculate cooldown progress for dodge/block
        const dodgeCooldownTicks =
          matchState.player.dodgeCooldownUntil - matchState.tick;
        const blockCooldownTicks =
          matchState.player.blockCooldownUntil - matchState.tick;
        const dodgeCooldown = Math.max(
          0,
          dodgeCooldownTicks /
            (DEFAULT_CONFIG.dodgeCooldown * DEFAULT_CONFIG.tickRate),
        );
        const blockCooldown = Math.max(
          0,
          blockCooldownTicks /
            (DEFAULT_CONFIG.blockCooldown * DEFAULT_CONFIG.tickRate),
        );

        hudRenderer.render({
          phase: matchState.phase,
          tick: matchState.tick,
          // Countdown and banners show the clock of the round they lead into
          // (Game Design signoff): 0:10 before R1, 0:00 at the handoff, 0:20
          // before R3. Only a live round clock turns pink.
          timeLeft: tutorialRun
            ? Infinity
            : ((
                {
                  intro: DEFAULT_CONFIG.roundGatherDuration,
                  countdown: DEFAULT_CONFIG.roundGatherDuration,
                  banner_r1: DEFAULT_CONFIG.roundGatherDuration,
                  banner_r2: 0,
                  banner_r3: DEFAULT_CONFIG.roundThrowDuration,
                } as Record<string, number>
              )[matchState.phase] ?? timeLeft),
          tutorialStep: tutorialRun
            ? ((matchRound(matchState.phase) || 1) as 1 | 2 | 3)
            : undefined,
          playerHP: matchState.player.hp,
          opponentHP: matchState.opponent.hp,
          playerSnow: matchState.player.snow,
          playerAmmo: matchState.player.ammo,
          viewport: currentViewport,
          safeInsets: platform.safeInsets,
          playerDamage: matchState.player.damageDealt,
          opponentDamage: matchState.opponent.damageDealt,
          gameState: states.state,
          layoutRects: currentLayout,
          dodgeCooldown,
          blockCooldown,
          paused: matchState.paused,
          confirmingForfeit,
          koTime: presentation?.winType === "KO" ? presentationTime : undefined,
          forceRedraw: !!presentation,
          reduceMotion: platform.reduceMotion,
          buttonPress,
          fx: hudFx,
          opponentName: snowmanSpec(matchConfig.snowmanLevel ?? 1).name,
          opponentMaxHP: matchConfig.opponentHP ?? matchConfig.maxHP,
          dodgeSeconds: Math.max(0, dodgeCooldownTicks) / matchConfig.tickRate,
          blockSeconds: Math.max(0, blockCooldownTicks) / matchConfig.tickRate,
          windUp: matchState.projectiles.some(
            (p) => p.thrower === "opponent" && matchState.tick < p.endTick,
          ),
          heads: {
            player: renderer.getHeadPoint("player"),
            opponent: renderer.getHeadPoint("opponent"),
          },
          opponentTop: renderer.getBounds("opponent")?.y,
        });
      } else if (states.state.kind === "results") {
        const coins = rewardTimeline(
          resultTime,
          platform.reduceMotion,
          states.state.winType === "forfeit",
          resultRewards({
            ...states.state,
            playerHP: matchController?.getState().player.hp ?? 0,
            opponentHP: matchController?.getState().opponent.hp ?? 0,
          }),
        ).coins;
        if (Math.floor(coins / 10) > coinTick) {
          coinTick = Math.floor(coins / 10);
          audioManager.play("tick");
        }
        // Show results screen
        const finalState = matchController?.getState();
        hudRenderer.render({
          phase: "results",
          timeMs: resultTime,
          reduceMotion: platform.reduceMotion,
          tick: 0,
          timeLeft: 0,
          playerHP: finalState?.player.hp || 0,
          opponentHP: finalState?.opponent.hp || 0,
          playerSnow: 0,
          playerAmmo: 0,
          viewport: currentViewport,
          safeInsets: platform.safeInsets,
          playerDamage: finalState?.player.damageDealt || 0,
          playerHits: finalState?.player.hits ?? 0,
          playerBlocks: finalState?.player.blocks ?? 0,
          opponentDamage: finalState?.opponent.damageDealt || 0,
          gameState: states.state,
          layoutRects: currentLayout,
        });
      } else {
        // Clear HUD on menu
        hudRenderer.clear();
      }

      if (presentation)
        hudRenderer.drawEnd(
          presentationTime,
          presentation.winType === "KO",
          platform.reduceMotion ?? false,
        );
      if (platform.qa) endTextFrame();
      if (!qaStepping && !renderOnly) platform.requestAnimationFrame(frame);
    } catch (error) {
      if (platform.debug || platform.qa) {
        window.__frameErrors = (window.__frameErrors ?? 0) + 1;
      }
      console.error("Frame error:", error);
      if (!qaStepping && !renderOnly) platform.requestAnimationFrame(frame);
    }
  };
  document.addEventListener("visibilitychange", () => {
    if (document.hidden && matchController && states.state.kind === "match") {
      matchController.pause();
    }
  });

  // Start the animation loop
  void Promise.all([loadCanvasFonts(), renderer.ready])
    .then(() => {
      states.completeBoot(platform.profile);
      document.getElementById("splash")?.remove();
      if (platform.qa) {
        const params = new URLSearchParams(window.location.search);
        const requested = params.get("outcome");
        const seeds: Record<string, [number, number, string]> = {
          ko: [70, 0, "KO"],
          close: [60, 50, "decision"],
          decision: [90, 40, "decision"],
          "lose-close": [50, 60, "decision"],
          "lose-decision": [40, 80, "decision"],
          "lose-ko": [0, 80, "KO"],
          forfeit: [70, 80, "forfeit"],
          draw: [50, 50, "draw"],
        };
        if (requested && seeds[requested]) {
          startNewMatch();
          window.__snowballQA?.forceResult(...seeds[requested]);
        } else if (
          params.get("forceKO") === "1" ||
          params.get("forceResult") === "ko"
        ) {
          window.__qa?.knockout(
            params.get("loser") === "player" ? "player" : "opponent",
          );
        }
      }
      if (platform.qa && presentation?.winType === "KO") {
        const koTime = new URLSearchParams(window.location.search).get(
          "koTime",
        );
        if (koTime !== null)
          window.__qa?.freeze(Math.max(0, Number(koTime) || 0));
      }
      if (!qaStepping) platform.requestAnimationFrame(frame);
    })
    .catch((error: unknown) => {
      console.error("Asset preload failed:", error);
      states.completeBoot(platform.profile);
      document.getElementById("splash")?.remove();
      if (!qaStepping) platform.requestAnimationFrame(frame);
    });

  return {
    resize: (width, height, pixelRatio, safeInsets) => {
      platform.width = width;
      platform.height = height;
      platform.pixelRatio = pixelRatio;
      platform.safeInsets = safeInsets;
      renderer.resize(width, height, pixelRatio, safeInsets);
      hudRenderer.resize(width, height);
      currentViewport = computeGameViewport(width, height, safeInsets);
      currentLayout = computeLayoutRects(
        currentViewport,
        safeInsets,
        width,
        height,
      );
      inputManager?.setButtonRects(currentLayout.dodge, currentLayout.block);
    },
  };
}

declare global {
  interface Window {
    __qa?: {
      resultsFacing(): { hero: number | null; side: number | null };
      manual(enabled: boolean): void;
      step(n: number): void;
      phase(): string;
      t(): number;
      ids(): string[];
      bbox(id: string): { x: number; y: number; w: number; h: number } | null;
      hitRect(
        id: string,
      ): { x: number; y: number; w: number; h: number } | null;
      opponentName(): string;
      textSizes(): {
        id: string;
        text: string;
        pt: number;
        color: string;
        strokeColor?: string;
        strokeWidth?: number;
        bbox: { x: number; y: number; w: number; h: number };
      }[];
      particles(kind: string): number;
      stats(): { drawCalls: number; tris: number; fps: number };
      safe(): { t: number; b: number; l: number; r: number };
      hideAll(hidden?: boolean): void;
      timeScale(): number;
      koFall(): number;
      worldImage(): Promise<string>;
      knockout(loser?: "player" | "opponent"): void;
      freeze(t: number | boolean | null): { ok: boolean; t: number };
      unbuilt(): string[];
      hasParticles(): boolean;
      rewards(): { coins: number; xp: number; chest: number } | null;
      frozen(): boolean;
      freezeAt(phase: string, t: number): void;
      hideHud(hidden?: boolean): void;
      unfreeze(): void;
    };
    __snowballQA?: {
      onTick(callback: () => void): () => void;
      schedule(tick: number, action: Record<string, unknown>): void;
      summary(): unknown;
      start(): void;
      act(action: Record<string, unknown>): void;
      pause(): void;
      resume(): void;
      forceResult(playerHP: number, opponentHP: number, how: string): void;
      freeze(frozen: boolean): void;
      framing(): unknown;
      layout(): unknown;
      hideAll(hidden: boolean): void;
    };
    __frameErrors?: number;
    __renderedFrames?: number;
    __snowballDebug?: {
      getState(): unknown;
      getLayoutRects(): unknown;
      getFrameCount(): number;
      getRenderStats(): { drawCalls: number; triangles: number };
      getBounds(
        id: string,
      ): { x: number; y: number; width: number; height: number } | null;
      advanceMatch?: (seconds: number) => void;
      forfeitMatch?: () => void;
    };
  }
}
